/* packages/apps/workoutsmart/src/appConfig.js */
import React from 'react';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AgentAvatar from './components/common/AgentAvatar';
import { lazyComponent } from './utils/lazyComponent';
import { locationConfig, activityConfig, equipmentConfig, workoutPlanConfig, workoutSessionConfig } from './config/domainConfig';
import { enhancementsConfig } from './config/enhancementsConfig';
import { sidebarSections, leftSidebarSections } from './config/app/sidebarConfig';
import { calendarAppConfig } from './config/app/calendarAppConfig';
import { contentTypesConfig } from './config/app/contentTypesConfig';
import { startSessionButtons } from './config/app/sessionConfig';
import { widgetConfig } from './config/app/widgetConfig';
import { chatTeamConfigs, teamSidebarConfigs } from './config/app/teamConfig';

const SmartPreChatForm = lazyComponent(() => import('./components/chat/SmartPreChatForm'));

export const agentEffectFilters = [
  {
    // Match messages sent TO InfoProcurerAgent
    matcher: (item) => item.to === 'InfoProcurerAgent',
    rendererKey: 'info_lookup'
  },
  {
    // Match messages sent TO InfoProcurerAgent
    matcher: (item) => item.to === 'ExerciseDefinitionAgent',
    rendererKey: 'activity_request'
  },
  {
    // Match completion messages FROM InfoProcurerAgent
    matcher: (item) => item.source === 'InfoProcurerAgent' && item.msg_type === 'AGENT_COMPLETE',
    rendererKey: 'info_processing'
  }
];

const appConfig = {
  appName: "WorkoutSmart.AI",
  team_name: 'welcome_group',
  defaultTeamName: 'welcome_group',
  appLogo: '/logo512.webp',
  appIcon: '/favicon.ico',
  supportEmail: "support@workoutsmart.ai",
  discordLink: "https://discord.gg/e3xtBsYgpc",
  localStoragePrefix: "workoutsmart_app",
  agentChatAvatar: <AgentAvatar />,
  appChatAvatar: null,
  rightSidebarTitle: '',
  showContextIndicator: false,
  includeResourceContextInChat: false,
  availableThemes: [
    { id: 'default', label: 'WorkoutSmart Standard' },
    { id: 'premium', label: 'WorkoutSmart Premium' },
    { id: 'cyber', label: 'CyberFit 2077 (High Contrast)' }
  ],
  agentList: [
    { name: 'AI Assistant', icon: <FitnessCenterIcon />, id: 'coach' },
  ],
  agentIcons: {
    'Assistant Agent': FitnessCenterIcon,
  },
  startSessionButtons,
  preChatFormComponent: SmartPreChatForm,
  calendarConfig: calendarAppConfig,
  leftSidebarSections,
  sidebarSections,
  contentTypes: contentTypesConfig,
  widgets: widgetConfig,
  chatTeamConfigs,
  teamSidebarConfigs,
  enhancements: enhancementsConfig,
  domainConfigs: {
    location: locationConfig,
    activity: activityConfig,
    equipment: equipmentConfig,
    workout_plan: workoutPlanConfig,
    workout_session: workoutSessionConfig,
  },
  libraryViews: [],
  onboardingTour: {
      steps: [
        {
          targetId: null,
          title: "Quick Tour",
          content: "Welcome to WorkoutSmart.AI! This quick tour shows you the basic features how to use and navigate the site.",
          placement: 'center',
        },
        {
          targetId: 'session-btn-default-chat',
          title: 'Chat Session',
          content: 'Start a general conversation with your AI assistant. Ask questions, talk to the assistant, locate videos, or just chat about your fitness journey.',
          placement: 'right',
          onBeforeShow: ({ ui }) => {
            ui.setLeftSidebarOpen(true);
            if (ui.isMobile) ui.setRightSidebarOpen(false);
          }
        },
        {
          targetId: 'session-btn-design-workout',
          title: 'Design a Workout',
          content: 'Work with the AI assistant to create a new, structured workout plan from scratch tailored for you. The assistant will guide you through the process.',
          placement: 'right',
          onBeforeShow: ({ ui }) => {
            ui.setLeftSidebarOpen(true);
            if (ui.isMobile) ui.setRightSidebarOpen(false);
          }
        },
        {
          targetId: 'session-btn-workout-session',
          title: 'Log Activity',
          content: 'Start logging a workout session. You can either be guided by a pre-made plan or simply log activities as you go (freestyle).',
          placement: 'right',
          onBeforeShow: ({ ui }) => {
            ui.setLeftSidebarOpen(true);
            if (ui.isMobile) ui.setRightSidebarOpen(false);
          }
        },
        {
          targetId: 'left-sidebar-user',
          title: 'Profile & Settings',
          content: 'Manage your subscription, profile and goals, or change app preferences like theme and voice settings here.',
          placement: 'right',
          onBeforeShow: ({ ui }) => {
            ui.setLeftSidebarOpen(true);
            if (ui.isMobile) ui.setRightSidebarOpen(false);
          }
        },
        {
          targetId: 'app-right-sidebar',
          title: 'Resources Library',
          content: 'This is your personal gym library. Access your calendar, custom workouts, workout logs, exercise database, equipment database, and statistics here.',
          placement: 'left',
          onBeforeShow: ({ ui }) => {
            ui.setRightSidebarOpen(true);
            if (ui.isMobile) ui.setLeftSidebarOpen(false);
          }
        },
        {
          targetId: 'chat-input-area',
          title: "You're Ready!",
          content: "That's it! Now talk with your assistant to get started with your WorkoutSmart.ai fitness journey!",
          placement: 'top',
          onBeforeShow: ({ ui }) => {
            ui.setLeftSidebarOpen(!ui.isMobile);
            ui.setRightSidebarOpen(!ui.isMobile);
          }
        }
      ]
  },
  chatSessionsDashboard: {
    title: 'Start a New Workout Session',
  },
  loginPage: {
    mainHeader: "Train Smarter, Not Harder.",
    mainSubheader: "Your personal AI assistant for creating effective workout plans, tracking progress, and achieving your fitness goals faster than ever.",
    formHeader: "Start Your Fitness Journey",
    formSubheader: "(No credit card required for trial)",
    footerSubtext: "Your path to peak fitness starts here.",
    ctaButtonText: "Start Your Free Trial",
    signUpButtonText: "Sign Up for Free",
    underlineWord: "Smarter",
    featureHighlights: [
      { icon: 'EmojiEvents', text: 'Achieve your fitness goals faster' },
      { icon: 'FormatListBulletedIcon', text: 'Guided session tracking: 1-tap logging' },
      { icon: 'InsightsIcon', text: 'Smart analysis and progression tracking' },
      { icon: 'Bolt', text: 'AI-powered personalized workout building and tracking' },
    ],
  },
  registerPage: {
    mainHeader: "Join the Revolution, Transform Today.",
    mainSubheader: "Create your free account and unlock AI-powered workout plans tailored to your goals, schedule, and fitness level.",
    formHeader: "Create Your Account",
    formSubheader: "Start your free trial today — no credit card required",
    footerSubtext: "Your path to peak fitness starts here.",
    loginButtonText: "Already have an account? Log In",
    createAccountButtonText: "Create Account",
    underlineWord: "Revolution",
  },
  customSections: {
    userProfile: {
      userSetup: lazyComponent(() => import('./components/user/UserSetupSection')),
      fitnessProfile: lazyComponent(() => import('./components/user/FitnessProfileSection')),
      userPreferences: lazyComponent(() => import('./components/user/UserPreferencesSection')),
    }
  },
  adminPanel: {
    dataExplorer: {
      resourceTypes: [
        { label: "Fitness Profile", value: "fitness_profile" },
        { label: "Workout Plan", value: "workout_plan" },
        { label: "Workout Session", value: "workout_session" },
        { label: "Workout Session Report", value: "workout_session_report" },
        { label: "Exercise Definition", value: "exercise_definition" },
        { label: "Exercise Definition Details", value: "exercise_definition_details" },
        { label: "Bio Logs", value: "bio_log" },
        { label: "Trackable Definitions", value: "trackable_item_definition" },
        { label: "Location", value: "location" },
        { label: "Equipment (Location Feature)", value: "location_feature" },
        { label: "Equipment Details (Location Feature Details)", value: "location_feature_details" },
        { label: "Dates", value: "date" },
        { label: "References", value: "reference" },
        { label: "User", value: "user" },
        { label: "User Profile", value: "user_profile" },
        { label: "User Preferences", value: "user_preferences" },
        { label: "User Metadata", value: "user_resource_metadata" },
        { label: "Measurement Type", value: "measurement_type" },
        { label: "Payment Plan", value: "payment_plan" },
        { label: "User Subscription", value: "user_subscription" },
        { label: "Daily Usage Record", value: "daily_usage_record" },
        { label: "Chat Transcript", value: "chat_transcript" },
        { label: "Chat Session", value: "chat_session" },
        { label: "Chat Session History", value: "chat_session_history" },
        { label: "Legal Notices", value: "legal_document" },
        { label: "User Consent Records", value: "user_consent" },
        { label: "Verification Code", value: "verification_code" },
      ],
    }
  },
  aiLimitationsNotice: {
    noticeId: "v1.1",
    title: "Safety & AI Limitations Notice",
    summary: "Tap to review important safety information before using AI fitness generated content.",
    items: [
      {
        title: "No Physical Observation",
        description:
          "This AI cannot see you, watch your form, or monitor your movements. It cannot detect if you are performing an exercise incorrectly or unsafely.",
      },
      {
        title: "No Knowledge of Your True Condition",
        description:
          "This AI has no awareness of your actual physical abilities, fitness level, injuries, pain, fatigue, or medical history beyond what you voluntarily provide — and it cannot verify the accuracy or completeness of that information or always incorporate such knowledge in it's generations.",
      },
      {
        title: "No Medical Competence",
        description:
          "This AI cannot diagnose, treat, or assess any medical condition. It is not a substitute for professional medical advice, physical therapy, or certified personal training.",
      },
      {
        title: "AI Can Be Wrong",
        description:
          "AI-generated suggestions may be inaccurate, inappropriate for your situation, or potentially harmful. This includes, but not limited to, the possibility of significant errors such as recommending exercises unsuitable for your condition, suggesting unsafe intensity levels, or failing to account for contraindications.",
      },
      {
        title: "You Assume All Risk",
        description:
          "By using this AI service, you acknowledge that you are solely responsible for evaluating whether any suggestion and information provided is safe and appropriate for you. Always consult a qualified physician before beginning any new exercise program, and stop immediately if you experience pain, dizziness, or discomfort.",
      },
    ],
    footer:
      "Use your own judgment, listen to your body, and when in doubt, consult a licensed healthcare or fitness professional.",
  },
  agentEffectFilters,
};

export default appConfig;